// Recovery-routes intent bridge data for the state waste-code clusters
// (Phase 2, docs/plans/2026-07-08-code-page-conversion-layer.md).
// Routes derive from the code family — TX form-code category (30 TAC
// §335.521(c) Appendix 3 groups), CA Appendix XII category, WA designation
// criteria — and trace to recognized recovery pathways. Dollar-free.
//
// @source 40 CFR 261.1(c)(4) — reclamation definition
// @source 40 CFR 261.4(a)(13) — scrap metal being recycled
// @source 40 CFR Part 279 — used oil management standards (re-refining)
// @source 40 CFR Part 266 Subparts F/H — precious metal recovery; energy recovery
// @source 40 CFR 268.40 / 268.42 Table 1 — LDR treatment standards; HTMR
// @source 40 CFR Part 761 — TSCA PCB decontamination and disposal
// @source WAC 173-303-100 — WA toxicity/persistence designation criteria
import type { TxFormCodeEntry, TxFormCategory } from './txWasteCatalogue';
import type { CaWasteCodeEntry, CaWasteCategory } from './caWasteCatalogue';
import type { WaWasteCodeEntry, WaCriteria } from './waWasteCatalogue';
import { route, FEDERAL_SOURCES as SRC } from './recoveryRouteTypes';
import type { RecoveryRoute } from './recoveryRouteTypes';

const WAC_CRITERIA = {
  source: 'WAC 173-303-100 — designation criteria',
  sourceUrl: 'https://app.leg.wa.gov/wac/default.aspx?cite=173-303-100',
};

// --- Shared routes -----------------------------------------------------

const SOLVENT_DISTILLATION = route(
  'Solvent distillation & reuse',
  'High',
  'Solvent-bearing liquid streams are prime reclamation candidates: distillation recovers a technical-grade solvent for reuse, with only the still bottoms remaining as waste.',
  SRC.reclamation,
);

const USED_OIL_REREFINING = route(
  'Used-oil re-refining',
  'High',
  'Waste oil and oily liquids route into the used-oil management system, where re-refining returns base oil to the lubricant market — a mature, established recovery chain.',
  SRC.usedOil,
);

const FUEL_BLENDING = route(
  'Fuel blending & energy recovery',
  'Medium',
  'Organic-bearing streams with usable fuel value can be blended and burned for energy recovery in permitted boilers and industrial furnaces instead of being managed as pure disposal.',
  SRC.energyRecovery,
);

const ACID_ALKALI_REGEN = route(
  'Acid / alkali regeneration',
  'Medium',
  'Spent acids and caustics are regeneration candidates: spent pickling and etching liquors can be processed for acid recovery or metal-salt by-products, depending on free-acid strength and dissolved metal load.',
  SRC.reclamation,
);

const METALS_RECOVERY = route(
  'Metals recovery (HTMR / hydrometallurgical)',
  'Medium',
  'Metal-bearing streams route to high temperature metals recovery or hydrometallurgical processing, where the contained metals are extracted and returned to commerce as secondary raw material.',
  SRC.htmr,
);

const NEUTRALIZATION = route(
  'On-site elementary neutralization',
  'Low',
  'Streams hazardous only for corrosivity can be treated in an elementary neutralization unit — a recognized unit type that manages the characteristic without a full treatment permit.',
  SRC.definitions,
);

const SCRAP_METAL = route(
  'Metal scrap recovery',
  'High',
  'Clean metal scale, scrap, drums and containers follow the standard scrap route — scrap metal being recycled sits outside most hazardous waste regulation entirely.',
  SRC.scrapMetal,
);

const CARBON_REGEN = route(
  'Spent activated carbon regeneration',
  'Medium',
  'Spent activated carbon loaded with organic constituents routes to carbon regeneration units, restoring the carbon for reuse instead of single-pass disposal.',
  SRC.definitions,
);

const STABILIZATION = route(
  'Stabilization & controlled disposal',
  'Low',
  'Below economic recovery concentrations, stabilization to the applicable treatment standard followed by controlled disposal remains the default management route.',
  SRC.ldr,
);

// --- Texas: routes per form-code category (30 TAC §335.521(c)) ----------

const TX_ROUTES: Record<TxFormCategory, RecoveryRoute[]> = {
  'lab-packs': [
    route(
      'Lab-pack consolidation & chemical redistribution',
      'Specialist',
      'Usable, on-spec laboratory chemicals pulled from lab packs can be redistributed as product; the remainder is consolidated by specialist processors for compliant downstream management.',
      SRC.ccpReclaim,
    ),
    route(
      'Energy recovery of the organic fraction',
      'Low',
      'Organic lab-pack contents with fuel value route to fuel blending for energy recovery in permitted industrial furnaces after specialist sorting.',
      SRC.energyRecovery,
    ),
  ],
  'liquids-inorganic': [ACID_ALKALI_REGEN, METALS_RECOVERY, NEUTRALIZATION],
  'liquids-organic': [SOLVENT_DISTILLATION, USED_OIL_REREFINING, FUEL_BLENDING],
  'solids-inorganic': [SCRAP_METAL, METALS_RECOVERY, STABILIZATION],
  'solids-organic': [FUEL_BLENDING, CARBON_REGEN, STABILIZATION],
  'sludges-inorganic': [METALS_RECOVERY, STABILIZATION],
  'sludges-organic': [
    FUEL_BLENDING,
    route(
      'Oil recovery from oily sludge',
      'Medium',
      'Oily sludges and still bottoms with recoverable oil content route through used-oil processors for oil recovery before the residual solids are treated.',
      SRC.usedOil,
    ),
    STABILIZATION,
  ],
  'gases-inorganic': [
    route(
      'Cylinder & gas reclamation',
      'Specialist',
      'Containerized gas streams are handled by specialist reclaimers: recoverable gases are repurified for reuse and cylinders are decommissioned or requalified.',
      SRC.reclamation,
    ),
    route(
      'Specialist treatment of non-recoverable gases',
      'Specialist',
      'Gases without a reclamation market are treated — scrubbed, neutralized or destroyed — at specialist facilities equipped for compressed-gas management.',
      SRC.ldr,
    ),
  ],
  'gases-organic': [
    route(
      'Refrigerant & organic gas reclamation',
      'Specialist',
      'Refrigerants and recoverable organic gases are repurified to specification by specialist reclaimers and returned to service instead of being vented or destroyed.',
      SRC.reclamation,
    ),
    FUEL_BLENDING,
  ],
  'plant-refuse': [
    FUEL_BLENDING,
    route(
      'Beneficial reuse of clean plant trash',
      'Medium',
      'Uncontaminated plant refuse — wood, cardboard, inert debris — separates into conventional recycling and beneficial reuse streams once segregated from regulated waste.',
      SRC.reclamation,
    ),
  ],
};

export function getTxRecoveryRoutes(entry: Pick<TxFormCodeEntry, 'category'>): RecoveryRoute[] {
  return TX_ROUTES[entry.category];
}

// --- California: routes per Appendix XII category ------------------------

const CA_ROUTES: Record<CaWasteCategory, RecoveryRoute[]> = {
  inorganics: [METALS_RECOVERY, ACID_ALKALI_REGEN, NEUTRALIZATION],
  organics: [SOLVENT_DISTILLATION, USED_OIL_REREFINING, FUEL_BLENDING],
  sludges: [METALS_RECOVERY, STABILIZATION],
  miscellaneous: [
    route(
      'Precious metal recovery from photochemicals',
      'High',
      'Silver-bearing photochemical and laboratory streams qualify for the reduced-burden precious metal recovery regime — silver is routinely refined back to metal.',
      SRC.preciousMetals,
    ),
    SCRAP_METAL,
    route(
      'Specialist stream review',
      'Specialist',
      'Miscellaneous category codes span very different materials; a constituent-level review identifies which fraction carries a recovery route and which needs treatment.',
      SRC.reclamation,
    ),
  ],
  restricted: [
    METALS_RECOVERY,
    route(
      'Specialist treatment below restriction thresholds',
      'Specialist',
      'California restricted wastes must be treated below the constituent thresholds before land disposal; treatment residues with metal value can continue to metals recovery.',
      SRC.ldr,
    ),
  ],
};

export function getCaRecoveryRoutes(entry: Pick<CaWasteCodeEntry, 'category'>): RecoveryRoute[] {
  return CA_ROUTES[entry.category];
}

// --- Washington: routes per designation criteria (WAC 173-303) -----------

const WA_ROUTES: Record<WaCriteria, RecoveryRoute[]> = {
  toxicity: [
    route(
      'Constituent-level recovery review',
      'Medium',
      'Toxicity-designated streams designate by equivalent concentration, not source — a constituent review can reveal recoverable solvent or metal fractions within the designated stream.',
      SRC.reclamation,
    ),
    route(
      'Treatment below designation thresholds',
      'Low',
      'Treatment that brings the equivalent concentration below the designation threshold takes the stream out of dangerous waste management, opening conventional routes.',
      WAC_CRITERIA,
    ),
  ],
  persistence: [
    route(
      'High-temperature combustion',
      'Specialist',
      'Halogenated organic and PAH persistence wastes route to combustion-based destruction at permitted units equipped for halogenated streams; persistence is why milder routes fail.',
      SRC.ldr,
    ),
    route(
      'Constrained energy recovery',
      'Low',
      'Streams with meaningful fuel value may qualify for burning for energy recovery, tightly constrained by halogen content limits at the receiving furnace.',
      SRC.energyRecovery,
    ),
  ],
  corrosivity: [ACID_ALKALI_REGEN, NEUTRALIZATION],
  pcb: [
    route(
      'Equipment decontamination for reuse',
      'Specialist',
      'PCB-contaminated equipment can be decontaminated to the TSCA decontamination standards and returned to service; the removed PCB fraction has no recovery route.',
      SRC.tscaPcb,
    ),
    route(
      'TSCA-compliant destruction',
      'Specialist',
      'PCB wastes route to TSCA-approved destruction — the PCB fraction itself is a pure liability stream with no recognized material recovery.',
      SRC.tscaPcb,
    ),
  ],
};

export function getWaRecoveryRoutes(entry: Pick<WaWasteCodeEntry, 'criteria'>): RecoveryRoute[] {
  return WA_ROUTES[entry.criteria];
}
